import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Typography
} from '@mui/material';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import ChildCareRoundedIcon from '@mui/icons-material/ChildCareRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DashboardPageHeader from 'pages/dashboard/DashboardPageHeader';
import VendorDashboardLayout from 'pages/dashboard/VendorDashboardLayout';

const percentage = (count, total) => (total ? Math.round((count / total) * 100) : 0);

function SplitStatCard({ title, icon: Icon, items }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
        <Stack direction="row" alignItems="center" gap={1} mb={2.5}>
          <Icon color="primary" fontSize="small" />
          <Typography variant="h6">{title}</Typography>
        </Stack>
        <Stack
          direction="row"
          divider={<Box sx={{ borderLeft: '1px solid', borderColor: 'divider' }} />}
        >
          {items.map((item) => (
            <Box
              key={item.label}
              sx={{ flex: 1, minWidth: 0, px: 2, '&:first-of-type': { pl: 0 } }}
            >
              <Typography
                variant="h4"
                sx={{ color: item.color, fontVariantNumeric: 'tabular-nums' }}
              >
                {item.value.toLocaleString('bg-BG')}
              </Typography>
              <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

function BreakdownRow({ item, total, largestCount, color }) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} mb={0.8}>
        <Typography variant="body2" fontWeight={650} noWrap title={item.name}>
          {item.name}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {percentage(item.count, total)}%
          </Typography>
          <Chip label={item.count.toLocaleString('bg-BG')} size="small" />
        </Stack>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={(item.count / largestCount) * 100}
        aria-label={`${item.name}: ${item.count} продукта`}
        sx={{
          bgcolor: 'grey.100',
          borderRadius: 999,
          height: 7,
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 999 }
        }}
      />
    </Box>
  );
}

function SectionDivider({ label, count, color }) {
  return (
    <Stack direction="row" alignItems="center" gap={1.25}>
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', flex: 1 }} />
      <Typography
        variant="overline"
        sx={{ color, fontSize: 11, fontWeight: 800, letterSpacing: '0.09em', lineHeight: 1 }}
      >
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {count.toLocaleString('bg-BG')}
      </Typography>
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', flex: 1 }} />
    </Stack>
  );
}

function BreakdownCard({ title, subtitle, items, sections, total, color }) {
  const displayedItems = sections ? sections.flatMap((section) => section.items) : items;
  const largestCount = Math.max(...displayedItems.map((item) => item.count), 1);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, '&:last-child': { pb: { xs: 2.5, md: 3.5 } } }}>
        <Typography variant="h6">{title}</Typography>
        <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5, mb: 3 }}>
          {subtitle}
        </Typography>

        {displayedItems.length === 0 ? (
          <Typography color="text.secondary">Няма налични данни.</Typography>
        ) : sections ? (
          <Stack spacing={3}>
            {sections.map(
              (section) =>
                section.items.length > 0 && (
                  <Stack key={section.key} spacing={2.25}>
                    <SectionDivider
                      label={section.label}
                      count={section.items.reduce((sum, item) => sum + item.count, 0)}
                      color={section.color}
                    />
                    {section.items.map((item) => (
                      <BreakdownRow
                        key={item.id}
                        item={item}
                        total={total}
                        largestCount={largestCount}
                        color={section.color}
                      />
                    ))}
                  </Stack>
                )
            )}
          </Stack>
        ) : (
          <Stack spacing={2.25}>
            {items.map((item) => (
              <BreakdownRow
                key={item.id}
                item={item}
                total={total}
                largestCount={largestCount}
                color={color}
              />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default function ProductStats() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadStats = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await axios.get('/productStats');
      setStats(response.data);
    } catch (requestError) {
      setError('Статистиката не можа да бъде заредена. Моля опитайте отново.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return (
    <VendorDashboardLayout>
      <DashboardPageHeader
        title="Статистика на продуктите"
        subtitle="Бърз преглед на каталога по вид, категория, материал и статус."
        icon={AnalyticsRoundedIcon}
        actions={
          <Button
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            onClick={loadStats}
            disabled={isLoading}
          >
            Обнови
          </Button>
        }
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={loadStats}>
              Опитай отново
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {isLoading && !stats ? (
        <Box
          sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: 320 }}
        >
          <CircularProgress aria-label="Зареждане на статистиката" />
        </Box>
      ) : (
        stats && (
          <>
            <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, #312e81 0%, #4f46e5 100%)',
                    color: 'white',
                    height: '100%'
                  }}
                >
                  <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography sx={{ color: 'rgba(255,255,255,.72)' }}>
                          Всички продукти
                        </Typography>
                        <Typography
                          variant="h3"
                          sx={{ fontWeight: 760, mt: 0.75, fontVariantNumeric: 'tabular-nums' }}
                        >
                          {stats.total.toLocaleString('bg-BG')}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          alignItems: 'center',
                          bgcolor: 'rgba(255,255,255,.14)',
                          borderRadius: 3,
                          display: 'flex',
                          height: 56,
                          justifyContent: 'center',
                          width: 56
                        }}
                      >
                        <Inventory2RoundedIcon sx={{ fontSize: 29 }} />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <SplitStatCard
                  title="По вид"
                  icon={ChildCareRoundedIcon}
                  items={[
                    { label: 'Основни', value: stats.audience.main, color: '#4f46e5' },
                    { label: 'Детски', value: stats.audience.kids, color: '#2563eb' }
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <SplitStatCard
                  title="По статус"
                  icon={CheckCircleRoundedIcon}
                  items={[
                    { label: 'Публикувани', value: stats.status.published, color: '#15803d' },
                    { label: 'Чернови', value: stats.status.draft, color: '#b45309' }
                  ]}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2.5}>
              <Grid item xs={12} lg={4}>
                <BreakdownCard
                  title="По категории"
                  subtitle="Брой продукти във всяка подкатегория"
                  sections={[
                    {
                      key: 'main',
                      label: 'Основни',
                      color: '#4f46e5',
                      items: stats.categories.filter((item) => item.group !== 'kids')
                    },
                    {
                      key: 'kids',
                      label: 'Детски',
                      color: '#2563eb',
                      items: stats.categories.filter((item) => item.group === 'kids')
                    }
                  ]}
                  total={stats.total}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <BreakdownCard
                  title="По материали"
                  subtitle="Брой продукти за всеки материал"
                  items={stats.materials}
                  total={stats.total}
                  color="#0f766e"
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <BreakdownCard
                  title="По типове"
                  subtitle="Брой продукти за всеки тип"
                  items={stats.productTypes || []}
                  total={stats.total}
                  color="#b45309"
                />
              </Grid>
              <Grid item xs={12}>
                <BreakdownCard
                  title="Материали по подкатегория"
                  subtitle="Брой продукти от всеки материал във всяка подкатегория"
                  sections={(stats.categoryMaterials || []).map((category) => ({
                    key: category.id,
                    label: category.name,
                    color: category.group === 'kids' ? '#2563eb' : '#4f46e5',
                    items: category.materials
                  }))}
                  total={stats.total}
                />
              </Grid>
            </Grid>
          </>
        )
      )}
    </VendorDashboardLayout>
  );
}
